import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { onIdTokenChanged } from 'firebase/auth';
import { getFirebaseAuth } from '@heyroot/auth-shared';
import { captureException } from '@heyroot/sentry-web';
import { authenticatedFetch } from './authenticated-fetch';
import type { AdminAuthState } from './router-context';

const Ctx = createContext<AdminAuthState | null>(null);
export const useAdminAuth = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error('no auth');
  return v;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AdminAuthState>({
    status: 'loading',
    uid: null,
    email: null,
    activeRole: null,
    availableRoles: [],
    permissionBits: 0n,
    hasPerm: () => false,
  });
  useEffect(
    () =>
      onIdTokenChanged(getFirebaseAuth(), async (user) => {
        if (!user) {
          setState((s) => ({ ...s, status: 'unauthenticated', hasPerm: () => false }));
          return;
        }
        // The whole body below is wrapped in one try/catch rather than
        // guarding each throwing call individually: nothing awaits this
        // async callback's return value (onIdTokenChanged doesn't await its
        // listener), so ANY uncaught exception here — a fetch failure
        // (network error, AuthNotReadyError), a malformed /api/me response
        // body (res.json()/BigInt parsing), etc. — becomes an unhandled
        // promise rejection app-wide (surfaced on staging as an unhandled
        // "Failed to fetch" error attributed to whichever route happened to
        // be mounted, e.g. /tenants).
        try {
          const res = await authenticatedFetch('/api/me');
          if (res.status === 403) {
            setState((s) => ({ ...s, status: 'not_provisioned', hasPerm: () => false }));
            return;
          }
          if (!res.ok) {
            setState((s) => ({ ...s, status: 'unauthenticated', hasPerm: () => false }));
            return;
          }
          // Guard: Firebase may have signed out (or switched users) during the async
          // /api/me round-trip. Check that the same user is still signed in so we
          // don't apply stale /api/me data to a different session. If the user changed,
          // the concurrent onIdTokenChanged for the new user will re-run this flow.
          if (getFirebaseAuth().currentUser?.uid !== user.uid) return;
          const me = await res.json();
          const bits = BigInt(me.permissionBits);
          setState({
            status: 'authenticated',
            uid: me.uid,
            email: me.email,
            activeRole: me.role,
            availableRoles: [me.role],
            permissionBits: bits,
            hasPerm: (b) => (bits & b) === b,
          });
        } catch (err) {
          // Never rethrow here (see comment above) — but still surface the
          // failure for debugging, matching the sibling catch blocks in
          // sidebar-footer.tsx and login.tsx. Without this, a real backend
          // regression (malformed /api/me body, missing permissionBits)
          // degrades to 'unauthenticated' with zero diagnostic trail.
          // console.error alone is a browser-console breadcrumb only, not a
          // Sentry event (no captureConsoleIntegration is registered) — this
          // catch sits on the app's entire auth path, so a silent swallow
          // here would mean a real /api/me outage pages nobody.
          // HEYROOT-STAGING-23: this capture is expected telemetry for a
          // transient connectivity failure, not a tenant-detail-page bug —
          // Sentry attributes it to whichever route happens to be mounted.
          console.error('[auth-context] /api/me failed', err);
          captureException(err, {
            captureContext: { tags: { source: 'auth-context.api-me-refresh' } },
          });
          setState((s) => ({ ...s, status: 'unauthenticated', hasPerm: () => false }));
        }
      }),
    [],
  );
  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
}
